"use client";

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import { clearToken, decodeToken, getToken, setToken, type DecodedToken } from "@/lib/auth";

type AuthContextValue = {
  user: DecodedToken | null;
  token: string | null;
  permissions: string[];
  permissionsLoading: boolean;
  authInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const fetchPermissions = async () => {
  const response = await api.get("/auth/me/permissions");
  const body = response.data as { permissions?: string[] };
  return Array.isArray(body.permissions) ? body.permissions : [];
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [token, setTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    const existing = getToken();
    if (existing) {
      const decoded = decodeToken(existing);
      if (decoded) {
        setTokenState(existing);
        setUser(decoded);
      } else {
        clearToken();
      }
    }
    setAuthInitialized(true);
  }, []);

  const permissionsQuery = useQuery({
    queryKey: ["permissions", user?.roleId],
    queryFn: fetchPermissions,
    enabled: Boolean(user?.roleId),
    staleTime: 1000 * 60 * 5,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const response = await api.post("/auth/login", { email, password });
      return response.data as { token: string };
    },
    onSuccess: (data) => {
      setToken(data.token);
      setTokenState(data.token);
      const decoded = decodeToken(data.token);
      setUser(decoded ?? null);
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
    },
  });

  const login = async (email: string, password: string) => {
    await loginMutation.mutateAsync({ email, password });
  };

  const logout = () => {
    clearToken();
    setTokenState(null);
    setUser(null);
    queryClient.clear();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  };

  const value = useMemo(
    () => ({
      user,
      token,
      permissions: permissionsQuery.data ?? [],
      permissionsLoading: permissionsQuery.isLoading,
      authInitialized,
      login,
      logout,
    }),
    [user, token, permissionsQuery.data, permissionsQuery.isLoading, authInitialized],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
